/**
 * <QuickActionRow> — the fixed grid of quick-action tiles (molecule).
 *
 * Two columns below the `md` breakpoint (800), one row of four above it — expressed as a
 * wrapping flex row with a breakpoint-driven `flexBasis`, so no JS branch is needed and the
 * layout follows a resize on its own.
 *
 * Tiles keep a **fixed place**: the caller passes the same ordered list every time, and the
 * grid never re-orders or hides one. Whether an action is possible is the tile's `disabled`,
 * not a missing tile.
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { QuickActionTile, type QuickActionTileProps } from "../QuickActionTile"

export type QuickActionRowProps = {
    actions: QuickActionTileProps[]
    testID?: string
}

export function QuickActionRow(props: QuickActionRowProps) {
    const { actions, testID } = props

    return (
        <QuickActionGrid testID={testID}>
            {actions.map((action) => (
                <QuickActionTile key={action.label} {...action} />
            ))}
        </QuickActionGrid>
    )
}

export type QuickActionGridProps = {
    children: React.ReactNode
    testID?: string
}

/**
 * La grille seule, chaque enfant dans sa cellule — exportée pour le squelette de l'accueil, dont
 * les tuiles tombent ainsi aux mêmes places sans recopier la grille.
 */
export function QuickActionGrid(props: QuickActionGridProps) {
    const { children, testID } = props

    return (
        <View style={styles.grid} testID={testID}>
            {React.Children.map(children, (child) => (
                <View style={styles.cell}>{child}</View>
            ))}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    grid: {
        flexDirection: "row",
        flexWrap: "wrap",
        gap: theme.spacing["2"],
    },
    cell: {
        // 2 per row under 800px, 4 above — the gap eats the remaining few percent.
        flexBasis: { xs: "48%", md: "23%" },
        flexGrow: 1,
        flexShrink: 1,
        // Floor below which a tile would clip its own label: the row wraps to fewer columns
        // instead. It is what makes the grid right in a narrow container, not only in a narrow window.
        minWidth: 150,
    },
}))
